import {
  BenefitCopyDataTypeRenewal,
  CoverAreaDataTypeRenewal,
  CoverDrawerDetailsDataType,
  CoverDrawerDetailsDataTypeRenewal,
  CoverLevelItemDataTypeRenewal,
  QuoteCoverDataTypeRenewal,
} from "@/schema/renewal-page/cover-level-page.schema";
import { CoverDrawerDetailsParent } from "@/schema/renewal-page/current-policy-page.schema";
import { MagnoliaNode } from "@/types/magnolia-utils";
import { getCoverLevelSeeMoreDetails } from "@/utils/get-cover-level-info";
import { normaliseMagnoliaResponse } from "@/utils/magnolia/magnolia.utils";

const getBenifitData = (data: Record<string, BenefitCopyDataTypeRenewal>) => {
  const keys = Object.keys(data);
  return keys.map((node) => {
    if (data[node].description) {
      return {
        description: data[node].description,
        isActive: data[node].isActive,
        isCancelation: data[node].isCancellation,
        isMedicalExpenses: data[node].isMedicalExpenses,
        isExcludeUK: data[node].isExcludeUK,
      };
    }
    return null;
  });
};

export const getCoverDrawerDetailsData = (
  data: CoverDrawerDetailsParent | undefined,
  policy: string,
  isCruise?: boolean
) => {
  if (!data) {
    return {
      coverDrawerTitle: "",
      coverDrawerCopy: "",
      coverDrawerDetails: [],
    };
  }

  const coverDrawerDetails = getCoverLevelSeeMoreDetails(
    data,
    policy,
    isCruise
  );
  const nodes = (coverDrawerDetails?.seeMoreDrawerDetails.coverDrawerDetails[
    "@nodes"
  ] ?? []) as unknown[] as string[];

  const drawerData = nodes
    .map((node) => {
      return {
        ...coverDrawerDetails?.seeMoreDrawerDetails.coverDrawerDetails[node],
        id: node,
      };
    })
    .filter(
      (drawer): drawer is NonNullable<typeof drawer> => drawer !== undefined
    );

  return {
    closeDrawerCta:
      coverDrawerDetails?.seeMoreDrawerDetails?.drawerCloseCta ?? "Close",
    coverDrawerTitle: coverDrawerDetails?.seeMoreDrawerDetails.coverName ?? "",
    coverDrawerCopy:
      coverDrawerDetails?.seeMoreDrawerDetails.coverDrawerCopy ?? "",
    coverDrawerDetails: drawerData,
  };
};

export const getComparisonTableDrawerDetailsData = (
  data: CoverDrawerDetailsDataTypeRenewal | null | undefined
) => {
  const drawerObj: CoverDrawerDetailsDataType = {};

  if (data) {
    Object.keys(data).forEach((key) => {
      const newKey = key.replace(
        "comparisonTableDrawerDetails",
        "comparisonTableData"
      );
      const temp = {
        isCruise: !!data[key].comparisonTableData?.isCruise,
        tripType: data[key].comparisonTableData?.tripType ?? null,
        ...normaliseMagnoliaResponse(
          data[key].comparisonTableData as unknown as MagnoliaNode
        ),
      };

      drawerObj[newKey] = temp;
    });
  }

  const compTempData = {
    comparisonTableSTRow: {},
    comparisonTableAMTRow: {},
    comparisonTableData: drawerObj,
  };

  return compTempData;
};

const getRelatedCoverLevelData = (
  data: CoverLevelItemDataTypeRenewal,
  coverDrawerDetails?: CoverDrawerDetailsParent,
  isCruise?: boolean
) => {
  return {
    coverType: data.coverType,
    coverTitle: data.coverName,
    ...getCoverDrawerDetailsData(coverDrawerDetails, data.coverType, isCruise),
    defaqtoRating: data.defaqtoRating,
    isSocialProofChipActive: data.isSocialProofChipActive ?? false,
    chipTopText: data.chipTopText ?? "",
    chipBottomText: data.chipBottomText ?? "",
    whichBestBuyBadge: data.whichBestBuyBadgeImage,
    benifitData: getBenifitData(data.benefitCopy).filter(
      (element) => element !== null
    ),
  };
};

const getRelatedCoverData = (
  data?: CoverAreaDataTypeRenewal,
  isCruise?: boolean,
  tripType?: string
) => {
  const dataNodes = data?.["0"].coverLevelBenefits as unknown as Record<
    string,
    QuoteCoverDataTypeRenewal
  >;
  const keys = Object.keys(dataNodes);

  return keys?.map((node) => {
    const dataSetCoverLevel = dataNodes[node]
      .coverBenefits as unknown as CoverLevelItemDataTypeRenewal;

    const cruise = dataSetCoverLevel?.isCruise ?? false;
    if (isCruise === cruise && tripType === "ANNUAL_MULTI_TRIP") {
      return getRelatedCoverLevelData(
        dataSetCoverLevel,
        data?.["0"].coverDrawerDetails as unknown as CoverDrawerDetailsParent,
        isCruise
      );
    }
    return null;
  });
};

export default getRelatedCoverData;
